import { useEffect, useRef } from "react";

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  opacity: number;
}

const STAR_COUNT_DESKTOP = 110;
const STAR_COUNT_MOBILE = 70;

const AnimatedBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (prefersReducedMotion.matches) {
      canvas.style.display = "none";
      return;
    }

    let animationFrame = 0;
    let width = 0;
    let height = 0;
    let stars: Star[] = [];
    let lastTime = 0;

    const starCount = window.innerWidth < 768 ? STAR_COUNT_MOBILE : STAR_COUNT_DESKTOP;

    const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min;

    const createStar = (): Star => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: randomBetween(0.6, 1.2),
      speed: randomBetween(25, 70),
      drift: randomBetween(-12, 12),
      opacity: randomBetween(0.18, 0.42),
    });

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const resetStar = (star: Star) => {
      star.x = Math.random() * width;
      star.y = -10;
      star.size = randomBetween(0.6, 1.2);
      star.speed = randomBetween(25, 70);
      star.drift = randomBetween(-12, 12);
      star.opacity = randomBetween(0.18, 0.42);
    };

    const setup = () => {
      resize();
      stars = Array.from({ length: starCount }, createStar);
    };

    const drawStar = (star: Star) => {
      context.beginPath();
      context.strokeStyle = `rgba(255,255,255,${star.opacity})`;
      context.lineWidth = star.size;
      context.moveTo(star.x, star.y);
      context.lineTo(star.x - star.drift * 0.08, star.y - 10);
      context.stroke();
    };

    const update = (time: number) => {
      const delta = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;

      context.clearRect(0, 0, width, height);

      stars.forEach((star) => {
        star.y += star.speed * delta;
        star.x += star.drift * delta;

        if (star.y > height + 20 || star.x < -20 || star.x > width + 20) {
          resetStar(star);
        }

        drawStar(star);
      });

      animationFrame = requestAnimationFrame(update);
    };

    setup();
    animationFrame = requestAnimationFrame(update);

    window.addEventListener("resize", setup);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", setup);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 z-0 pointer-events-none opacity-45"
      style={{ mixBlendMode: "screen" }}
    />
  );
};

export default AnimatedBackground;
